import { useTranslation } from "react-i18next"
import { format } from "date-fns"

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@f2e/ui/components/base"
import { CopyButton } from "@f2e/ui/components/Button"

import { TipIconButton } from "@/components"
import { Workspace, Agent } from "@/types"
import type { KnowledgeBaseFolder } from "@/hooks/api"
import { AgentInlineActions } from "./AgentInlineActions"
import { AgentSourceBadgeGroup } from "./AgentSourceBadgeGroup"

export interface CustomizeAgentTableProps {
  workspace: Workspace
  agents: Agent[]
  knowledgeBaseMap: Map<string, KnowledgeBaseFolder>
  onPlayground?: (agent: Agent) => void
  onDelete?: (agent: Agent) => void
}

const CustomizeAgentTable = ({
  workspace,
  agents,
  knowledgeBaseMap,
  onPlayground,
  onDelete,
}: CustomizeAgentTableProps) => {
  const { t } = useTranslation()

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="w-7/12">{t("workspace.name")}</TableHead>
          <TableHead className="w-2/12">{t("workspace.createdAt")}</TableHead>
          <TableHead className="w-2/12">{t("workspace.updatedAt")}</TableHead>
          <TableHead className="w-[140px] text-right">
            {t("workspace.actions")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {agents.map((agent) => (
          <TableRow key={agent.id}>
            <TableCell>
              <div className="flex items-center gap-2 flex-wrap">
                <HoverCard>
                  <HoverCardTrigger>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-wrap break-all text-left text-base"
                      onClick={() => onPlayground?.(agent)}
                    >
                      {agent.name}
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-full" align="start">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="truncate">{agent.id}</span>
                      <TipIconButton
                        tip={t("workspace.agentPlayground.copyAgentId")}
                        button={
                          <div className="transition duration-300 ease-in-out dark:hover:bg-slate-800 hover:bg-slate-100 rounded-sm">
                            <CopyButton
                              content={agent.id}
                              className="h-6 w-6 border-muted-foreground/20"
                            />
                          </div>
                        }
                      />
                    </div>
                  </HoverCardContent>
                </HoverCard>
                <AgentSourceBadgeGroup
                  agent={agent}
                  knowledgeBaseMap={knowledgeBaseMap}
                />
              </div>
            </TableCell>
            <TableCell className="whitespace-normal">
              {format(agent.createdAt, "yyyy-MM-dd HH:mm:ss")}
            </TableCell>
            <TableCell className="whitespace-normal">
              {format(agent.updatedAt, "yyyy-MM-dd HH:mm:ss")}
            </TableCell>
            <TableCell>
              <AgentInlineActions
                className="justify-end"
                agent={agent}
                workspace={workspace}
                onPlayground={onPlayground}
                onDelete={onDelete}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export { CustomizeAgentTable }
export default CustomizeAgentTable
